import React, { useState, useEffect, useMemo } from 'react';
import laboratoireService from '../../services/laboratoireService';
import authService from '../../services/authService';
import FournisseursLabosSwitch from '../../components/FournisseursLabosSwitch';
import { IcPlus, IcSearch, IcClose, IcFlask, IcPencil, IcTrash, IcSave, IcChevronLeft, IcChevronRight } from '../../theme/icons';
import { BtnSk, TableSkeleton } from '../../components/Skeleton';
import { filterSearch } from '../../utils/search';


// Formulaire (Brand Kit §05) : champ .inp 42px bordé #D1D5DB rayon 6,
// 13.5px, padding 11, focus 2px encre ; libellé 12px 500 encre, mb 4.
const inputClass = 'inp w-full px-3.5 py-2.5 rounded-xl text-base text-gray-900';
const textareaClass = 'inp w-full px-3.5 py-2.5 rounded-xl text-base text-gray-900';
const labelClass = 'block text-sm font-medium text-gray-900 mb-1.5';
// Pagination (owner 2026-09-09) : même page de 10 que « Liste des produits ».
const PAGE_SIZE = 10;

const LaboratoireList = () => {
  // Sellers can browse laboratoires but not create/edit/delete them.
  const canManage = authService.isManagerOrAdmin();
  const [laboratoires, setLaboratoires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Add / edit modal
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null); // laboratoire being edited, or null for create
  const [form, setForm] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fetchLaboratoires = async () => {
    try {
      setLoading(true);
      const data = await laboratoireService.getAllLaboratoires();
      setLaboratoires(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      console.error('Error fetching laboratoires:', err);
      setError('Impossible de charger les laboratoires.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLaboratoires();
  }, []);

  // Règle Ki Para (utils/search.js) : début de mot (nom, description), sans
  // accents, « commence par » en tête.
  const filtered = useMemo(
    () => filterSearch(laboratoires, searchTerm, { text: (l) => [l.name, l.description] }),
    [laboratoires, searchTerm],
  );

  // Pagination côté client (la liste est déjà chargée en entier) — mêmes
  // calculs et même pied que ProductList : compte à gauche, pages 1 / …
  // / n-1 n n+1 / … / dernière, chevrons .btn-day.
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [searchTerm]);
  const totalCount = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, totalCount);
  const displayed = useMemo(
    () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filtered, page]
  );
  const pageButtons = useMemo(() => {
    const pages = new Set([1, totalPages, page, page - 1, page + 1]);
    return [...pages]
      .filter((p) => p >= 1 && p <= totalPages)
      .sort((a, b) => a - b);
  }, [page, totalPages]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', description: '' });
    setFormError(null);
    setShowModal(true);
  };

  const openEdit = (lab) => {
    setEditing(lab);
    setForm({ name: lab.name || '', description: lab.description || '' });
    setFormError(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setFormError(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setFormError('Le nom du laboratoire est requis.');
      return;
    }
    try {
      setSaving(true);
      setFormError(null);
      const payload = { name: form.name.trim(), description: form.description.trim() };
      if (editing) {
        await laboratoireService.updateLaboratoire(editing.id, payload);
      } else {
        await laboratoireService.createLaboratoire(payload);
      }
      setShowModal(false);
      await fetchLaboratoires();
    } catch (err) {
      console.error('Error saving laboratoire:', err);
      setFormError(err.message || "Une erreur est survenue lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (lab) => {
    if (!window.confirm(`Supprimer le laboratoire « ${lab.name} » ?`)) return;
    try {
      setDeletingId(lab.id);
      await laboratoireService.deleteLaboratoire(lab.id);
      await fetchLaboratoires();
    } catch (err) {
      console.error('Error deleting laboratoire:', err);
      alert("Échec de la suppression du laboratoire. Il est peut-être lié à des produits.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="w-full px-4 pt-2 pb-4">
      {/* Owner 2026-09-09 : même en-tête que /suppliers = la bande d'onglets de
          « Nouvelle vente » (VenteTabs) — chips Fournisseurs | Laboratoires
          .tab-chip / .tab-chip-on (42px, gap-2), mb-4 ; mini « + » (btn-mini, « Ajouter un laboratoire »)
          à droite (ml-auto). */}
      <div className="mb-4">
        <div className="-mx-1 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
          {/* h-[46px] = la rangée de titre de « Liste des produits » (la pastille
              Promo y fait 46 px) : le chip et la barre tombent au même pixel. */}
          <div className="flex gap-2 px-1 min-w-full items-center h-[46px]">
            <FournisseursLabosSwitch variant="chips" />
            {canManage && (
              <div className="ml-auto inline-flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={openCreate}
                  className="h-9 w-9 flex items-center justify-center btn-mini transition-colors"
                  title="Ajouter un laboratoire"
                  aria-label="Ajouter un laboratoire"
                >
                  <IcPlus />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Barre de recherche = « Nouvelle vente » / « Liste des produits »
          (owner 2026-09-09) : pleine largeur, flex gap-3 mb-3, .inp h-12
          pl-11 pr-10, loupe #6B7280 à left-3.5, ✕ à right-3.5. */}
      <div className="flex gap-3 mb-3">
        <div className="relative flex-1 min-w-0">
          <IcSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Rechercher un laboratoire..."
            className="inp h-12 w-full pl-11 pr-10"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <IcClose />
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <TableSkeleton rows={10} columns={[
              { label: 'Laboratoire', avatar: true, w: 300 },
              { label: 'Description', w: 80 },
              { label: 'Actions', align: 'right', w: 60 },
            ]} />
      ) : filtered.length === 0 ? (
        /* État vide (Brand Kit §05) : cadre pointillé, icône 22px, phrase 13px 600 */
        <div className="empty-ki rounded-2xl p-12">
          <IcFlask size={36} className="empty-ki-icon w-9 h-9 mb-3" />
          <p className="empty-ki-title text-base mt-0">
            {searchTerm ? 'Aucun laboratoire correspondant à la recherche' : 'Aucun laboratoire enregistré'}
          </p>
        </div>
      ) : (
        /* Liste = celle de « Liste des produits » (owner 2026-09-09) : carte
           blanche rayon 16 bordée gray-200 > overflow-x-auto, en-tête th-ki
           px-5 py-3 text-xs, lignes td-ki px-5 py-4 text-sm survol .tr-ki,
           nom 600. Contenu des lignes = l'ancien (owner 2026-09-09, « back
           content ») : pastille fiole grise bordée. Actions conservées.
           Hauteur (owner 2026-09-09, dernier mot) : comme « Liste des
           produits » — 10 lignes de 81 px + pied de pagination, la carte prend
           la hauteur de ses lignes, la page défile exactement comme /products. */
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr>
                <th className="th-ki px-5 py-3 text-xs text-left uppercase">Laboratoire</th>
                <th className="th-ki px-5 py-3 text-xs text-left uppercase">Description</th>
                {canManage && <th className="th-ki px-5 py-3 text-xs text-right uppercase">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {displayed.map((lab) => (
                <tr key={lab.id} className="border-t border-gray-100 tr-ki transition-colors">
                  <td className="td-ki px-5 py-4 text-sm">
                    <div className="flex items-center gap-3">
                      {/* 48 px = l'image produit de « Liste des produits » → lignes de 81 px */}
                      <span className="h-12 w-12 rounded-xl bg-gray-100 text-gray-700 border border-gray-500 flex items-center justify-center text-sm shrink-0">
                        <IcFlask />
                      </span>
                      <span className="font-semibold text-gray-900">{lab.name}</span>
                    </div>
                  </td>
                  <td className="td-ki px-5 py-4 text-sm text-gray-500 max-w-md">
                    {lab.description ? lab.description : <span className="text-gray-300">—</span>}
                  </td>
                  {canManage && (
                    <td className="td-ki px-5 py-4 text-sm">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => openEdit(lab)}
                          title="Modifier"
                          className="h-9 w-9 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-200 transition-colors"
                        >
                          <IcPencil />
                        </button>
                        <button
                          onClick={() => handleDelete(lab)}
                          disabled={deletingId === lab.id}
                          title="Supprimer"
                          className={`h-9 w-9 flex items-center justify-center rounded-lg text-red-600 hover:bg-red-50 transition-colors ${deletingId === lab.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <IcTrash />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          {/* Pied de pagination = celui de « Liste des produits » (Composants-2
              §4) : compte total à gauche 12.5px gris tabulaire, pages en carrés
              34px, dernière page largeur auto. */}
          <div className="flex flex-col sm:flex-row items-center px-5 py-4 border-t border-gray-100 bg-white gap-3">
            <div className="text-[12.5px] text-gray-500 tabular-nums flex-1 w-full sm:w-auto">
              {totalCount === 0 ? '0 laboratoire' : `${rangeStart}–${rangeEnd} sur ${totalCount} laboratoire${totalCount > 1 ? 's' : ''}`}
            </div>
            <div className="inline-flex items-center gap-1">
              <button
                type="button"
                className="btn-day px-3 py-2 text-sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                aria-label="Page précédente"
              >
                <IcChevronLeft className="w-3 h-3" />
              </button>
              {pageButtons.map((p, idx) => {
                const prev = pageButtons[idx - 1];
                const gap = prev && p - prev > 1;
                const wide = p === totalPages && p > 1;
                return (
                  <React.Fragment key={p}>
                    {gap && <span className="pg-dots w-auto h-auto px-2">…</span>}
                    <button
                      type="button"
                      className={`${p === page ? 'pg-on' : 'pg-btn'}${wide ? ' w-auto px-2' : ''}`}
                      onClick={() => setPage(p)}
                    >
                      {p}
                    </button>
                  </React.Fragment>
                );
              })}
              <button
                type="button"
                className="btn-day px-3 py-2 text-sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                aria-label="Page suivante"
              >
                <IcChevronRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add / Edit modal */}
      {showModal && (
        <div className="modal-backdrop flex items-center justify-center z-50 p-4" onClick={closeModal}>
          <div className="bg-white rounded-2xl shadow-lg w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head p-5">
              <h3 className="modal-title text-lg">
                {editing ? 'Modifier le laboratoire' : 'Nouveau laboratoire'}
              </h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-white text-xl" aria-label="Fermer"><IcClose /></button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="p-6 space-y-4">
                {formError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">{formError}</div>
                )}
                <div>
                  <label className={labelClass} htmlFor="name">Nom <span className="req">*</span></label>
                  <input type="text" id="name" name="name" value={form.name} onChange={handleFormChange}
                    className={inputClass} placeholder="Nom du laboratoire" required />
                </div>
                <div>
                  <label className={labelClass} htmlFor="description">Description</label>
                  <textarea id="description" name="description" value={form.description} onChange={handleFormChange}
                    className={`${textareaClass} min-h-[100px]`} placeholder="Description (optionnel)" />
                </div>
              </div>

              {/* Pied de modale (Brand Kit §05) : 11/16px, filet gray-100, gap 8 */}
              <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-100">
                <button type="button" onClick={closeModal}
                  className="px-5 py-2.5 btn-g transition-colors">
                  Annuler
                </button>
                <button type="submit" disabled={saving}
                  className="flex items-center py-2.5 px-6 btn-p transition-colors">
                  {saving ? (
                    <>
                      <BtnSk />
                      Enregistrement...
                    </>
                  ) : (
                    <><IcSave className="mr-2" /> {editing ? 'Mettre à jour' : 'Enregistrer'}</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LaboratoireList;
